/* eslint-disable jsx-a11y/anchor-is-valid */
import { useActivation } from '@context/MobileOne';
import { ApiError, ButtonPrimary, DropdownList, FocusFirstError, FormInputField, ScrollToTop } from '@core/index';
import Icons from '@core/Utils/Icons/Icons';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { sanitizeRichText } from '@utils/sanitizeHtml';
import { appButtonType, appIcons, appIconsPath } from '@utils/globalConstant';
import { Form, Formik } from 'formik';

export function EditMnpData() {
  // Contexts
  const {
    isLoading,
    formError,
    phoneNumberActivationForm,
    setPhoneNumberActivationForm,
    phoneNumberValidationsWithMNP,
    simProvider,
    setStep9SubStep,
    simProviderError,
    areaCode,
    withMNPError,
    setWithMNPError
  } = useActivation();

  // const
  const headerContent = tA11y('ek_reg_change_mnp_hdl1');
  const provideLabelContent = tA11y('ek_global_prvdr_lbl');
  const providePlaceHolderContent = tA11y('ek_global_prvdr_plc');
  const oldNumberLabelContent = tA11y('ek_global_mnp_nmbr_lbl');
  const oldNumberPlaceHolderContent = tA11y('ek_global_mnp_nmbr_plc');
  const buttonContent = tA11y('ek_reg_change_btn1');

  // Function
  const onBackClick = () => {
    setStep9SubStep(0);
  };

  const handleAPIErrorLink = () => {
    const firstInput = document.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    }
  };

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100">
          <AppPageContentWrapper>
            <div className="mnp-activation w-100">
              <button
                type="button"
                className="border-0 bg-transparent m-0 p-0 mb-32"
                aria-label={tA11y('ek_global_icn-back_aria').ariaLabel}
                onClick={onBackClick}>
                <Icons name="ARROW_BACK" height={24} colorType='dark'/>
              </button>

              {headerContent?.content !== '' && headerContent?.content !== undefined && (
                <>
                  {headerContent?.isHtml ? (
                    <h2
                      className="ek-h2 text-primary mb-40"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(headerContent?.content) }}
                    />
                  ) : (
                    <h2 className="ek-h2 text-primary mb-40">{headerContent?.content}</h2>
                  )}
                </>
              )}

              <Formik
                initialValues={{ ...phoneNumberActivationForm, isCheckboxChecked: false }}
                validationSchema={phoneNumberValidationsWithMNP}
                onSubmit={(values) => {
                  const { oldNumberComplete } = values;
                  const oldNumberPrefix = oldNumberComplete.slice(0, 4);
                  const oldNumber = oldNumberComplete.slice(4);

                  const checkAreaCode = areaCode?.find(
                    (code) => code.value === oldNumberPrefix
                  );
                  if (!checkAreaCode) {
                    setWithMNPError(true);
                    return;
                  }
                  setWithMNPError(false);
                  setPhoneNumberActivationForm({
                    ...phoneNumberActivationForm,
                    ...values,
                    oldNumberPrefix,
                    oldNumber
                  });
                  setStep9SubStep(0);
                }}>
                {({
                  values: { currentProvider, oldNumberComplete },
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  touched,
                  errors
                }) => (
                  <Form onSubmit={handleSubmit} noValidate>
                    <DropdownList
                      id="currentProvider"
                      name="currentProvider"
                      label={provideLabelContent?.content}
                      value={currentProvider}
                      placeholder={providePlaceHolderContent?.content}
                      onChange={(callbackValue) => {
                        setWithMNPError(false);
                        setFieldValue('currentProvider', callbackValue);
                      }}
                      onBlur={handleBlur}
                      options={simProvider.map((provider) => ({
                        id: provider.id,
                        label: provider.value
                      }))}
                      valid={touched.currentProvider && !errors.currentProvider && !withMNPError}
                      invalid={(touched.currentProvider && errors.currentProvider) || withMNPError}
                      invalidMessage={
                        touched.currentProvider && errors.currentProvider
                          ? errors.currentProvider
                          : null
                      }
                      isRequired
                      aria-describedby="currentProvider-error"
                    />
                    <div className="space-32" />
                    <FormInputField
                      label={oldNumberLabelContent?.content}
                      placeholder={oldNumberPlaceHolderContent?.content}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      id="oldNumberComplete"
                      name="oldNumberComplete"
                      value={oldNumberComplete}
                      onChange={(e) => {
                        setWithMNPError(false);
                        handleChange({
                          target: {
                            id: 'oldNumberComplete',
                            name: 'oldNumberComplete',
                            value: e.target.value
                          }
                        });
                      }}
                      onClear={() => {
                        setFieldValue('oldNumberComplete', '');
                        setWithMNPError(false);
                      }}
                      onBlur={handleBlur}
                      valid={touched.oldNumberComplete && !errors.oldNumberComplete}
                      invalid={(!!touched.oldNumberComplete && !!errors.oldNumberComplete) || withMNPError}
                      invalidMessage={
                        touched.oldNumberComplete && errors.oldNumberComplete
                          ? errors.oldNumberComplete
                          : null
                      }
                      maxLength={16}
                      aria-describedby="oldNumberComplete-error"
                    />
                    {simProviderError || withMNPError && (
                      <div className="form-text mt-40 ps-4" aria-live="polite">
                        <ApiError
                          isGenericApiError={!withMNPError}
                          message={withMNPError ? 'ek_global_mnp_prefix_err' : simProviderError}
                          handleAPIErrorLink={handleAPIErrorLink}
                        />
                      </div>
                    )}
                    <div className="w-100 d-flex justify-content-center">
                      <div className="col-xxl-5 col-lg-5 col-10 px-0 mt-40">
                        <ButtonPrimary
                          aria-label={tA11y('ek_global_icn-back_aria').ariaLabel}
                          iconLeft={appIconsPath.ARROW_BACK}
                          buttonType={appButtonType.SECONDARY}
                          buttonContent={buttonContent}
                          type="submit"
                          isLoading={isLoading}
                        />
                      </div>
                    </div>
                    <FocusFirstError />
                  </Form>
                )}
              </Formik>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default EditMnpData;
